import React, { useState, useEffect, useCallback } from 'react';
import { resolveImg, PLACEHOLDERS } from '../lib/images';

/**
 * <Img /> — the ONLY <img>-equivalent component in the app.
 *
 * Why:
 *   - Replaces 131 raw <img> tags that had ZERO `onError` handlers — when an
 *     image failed (network blip, expired CDN, slow connection, Safari ITP)
 *     the user saw the broken-image icon with nothing to indicate the fault.
 *   - Centralizes URL resolution so relative `/api/files/...` paths work
 *     identically on web, PWA, and Capacitor native (where same-origin
 *     resolution fails because the app shell is served from capacitor://).
 *
 * Behavior:
 *   1. Resolves `src` via resolveImg() so relative URLs become absolute.
 *   2. On the FIRST load failure, retries ONCE with a cache-buster query
 *      param — handles transient ingress 502s and Cloudflare hiccups
 *      without showing the user a broken icon.
 *   3. On the SECOND failure, swaps to the variant-specific placeholder
 *      (avatar / business / event / logo / brand / generic).
 *   4. Forwards `onLoad` / `onError` for callers that need to react.
 *
 * Usage:
 *   <Img src={user.picture}           variant="avatar"   alt="Member" />
 *   <Img src={profile.logo_url}       variant="business" alt="Company" />
 *   <Img src={event.image_url}        variant="event"    alt={event.title} />
 *   <Img src={leader.image_url}       variant="leader"   alt={leader.name} />
 *   <Img src="/brand/logo-color.png"  variant="brand"    alt="DCCF" />
 *
 * Backward compatibility: accepts the same className / style / loading /
 * decoding / data-testid / id / etc. props as a plain <img>, so dropping it
 * in is mostly a tag-name swap.
 */
const Img = React.forwardRef(({
  src,
  variant = 'generic',
  alt = '',
  fallback,           // optional explicit fallback URL — overrides variant default
  className,
  loading = 'lazy',
  decoding = 'async',
  onLoad,
  onError,
  retry = true,       // attempt one cache-busted retry before falling back
  ...rest
}, ref) => {
  const resolvedSrc = resolveImg(src);
  const placeholderUrl = fallback || PLACEHOLDERS[variant] || PLACEHOLDERS.generic;

  // useEffect [resolvedSrc] resets the displayed URL whenever the parent passes
  // a new src — otherwise switching between two profiles in a list would keep
  // showing the previous one's fallback after a single error.
  const [displaySrc, setDisplaySrc] = useState(resolvedSrc || placeholderUrl);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setDisplaySrc(resolvedSrc || placeholderUrl);
    setAttempt(0);
  }, [resolvedSrc, placeholderUrl]);

  const handleError = useCallback((ev) => {
    onError?.(ev);
    // Already showing the placeholder — don't loop.
    if (displaySrc === placeholderUrl) return;
    if (attempt === 0 && retry && resolvedSrc) {
      // First failure → retry once with a cache-buster. Handles transient
      // Cloudflare 5xx / proxy hiccups without showing a broken icon.
      setAttempt(1);
      const sep = resolvedSrc.includes('?') ? '&' : '?';
      setDisplaySrc(`${resolvedSrc}${sep}__r=${Date.now()}`);
      return;
    }
    // Retried (or retry disabled) — swap to placeholder.
    setAttempt(2);
    setDisplaySrc(placeholderUrl);
  }, [displaySrc, placeholderUrl, attempt, resolvedSrc, retry, onError]);

  // If src is missing entirely, render placeholder directly — no error event,
  // no console noise.
  if (!resolvedSrc) {
    return (
      <img
        ref={ref}
        src={placeholderUrl}
        alt={alt}
        className={className}
        loading={loading}
        decoding={decoding}
        {...rest}
      />
    );
  }

  return (
    <img
      ref={ref}
      src={displaySrc}
      alt={alt}
      className={className}
      loading={loading}
      decoding={decoding}
      onLoad={onLoad}
      onError={handleError}
      {...rest}
    />
  );
});

Img.displayName = 'Img';

export default Img;
export { Img };
